import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { User } from '@angular/fire/auth';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { MessageModule } from 'primeng/message';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFacebook, faGoogle, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faCalendar, faMap, faMobileAlt, faShieldAlt, faStar, faUserPlus, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { CustomerService } from '../../customer/customer.service';
import { TransportationAgencyService } from '../../services/transportation-agency.service';
import { FreightForwarderService } from '../../services/freight-forwarder.service';
import { EnterpriseFeatureService } from '../../services/enterprise-feature.service';
import { ToolbarService } from '../../services/toolbar.service';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { ProgressBarModule } from 'primeng/progressbar';

type SignInAction =
  | 'phone-code'
  | 'microsoft'
  | 'google'
  | 'facebook'
  | 'create-account';

type PhoneStep = 'idle' | 'sending' | 'code-sent' | 'verifying' | 'verified';

const NO_CUSTOMER_PROFILE_ERROR = 'NO_CUSTOMER_PROFILE';
const REGISTRATION_REDIRECT_DELAY_MS = 2500;

interface SignInOption {
  labelKey: string;
  icon: IconDefinition;
  action: SignInAction;
}

interface FeatureHighlight {
  title: string;
  description: string;
  icon: IconDefinition;
}

@Component({
  selector: 'app-login',
  standalone: true,
  templateUrl: './login.html',
  styleUrls: ['./login.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    DividerModule,
    CheckboxModule,
    MessageModule,
    FontAwesomeModule,
    FloatLabelModule,
    TooltipModule,
    TranslateModule,
    ProgressBarModule
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  public readonly languageService = inject(LanguageService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly customerService = inject(CustomerService);
  private readonly transportationAgencyService = inject(TransportationAgencyService);
  private readonly freightForwarderService = inject(FreightForwarderService);
  private readonly enterpriseFeatureService = inject(EnterpriseFeatureService);
  private readonly toolbarService = inject(ToolbarService);
  private readonly baseMasterDataService = inject(BaseMasterDataService);

  readonly loginForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    phone: ['', [Validators.pattern(/^\+?[0-9\s().-]{6,24}$/)]],
    rememberMe: [true]
  });

  readonly otpForm: FormGroup = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
  });

  readonly signInOptions = computed<SignInOption[]>(() => {
    this.languageService.currentLanguage();
    return [
      { labelKey: this.authService.usesWhatsAppOtp ? 'LOGIN.WHATSAPP_CODE' : 'LOGIN.PHONE_CODE', icon: faMobileAlt, action: 'phone-code' },
      { labelKey: 'LOGIN.MICROSOFT', icon: faMicrosoft, action: 'microsoft' },
      { labelKey: 'LOGIN.GOOGLE', icon: faGoogle, action: 'google' },
      { labelKey: 'LOGIN.FACEBOOK', icon: faFacebook, action: 'facebook' },
      { labelKey: 'LOGIN.CREATE_ACCOUNT', icon: faUserPlus, action: 'create-account' }
    ];
  });

  readonly featureHighlights = computed<FeatureHighlight[]>(() => {
    this.languageService.currentLanguage();
    return [
      {
        title: this.translate.instant('LOGIN.FEATURE_AI'),
        description: this.translate.instant('LOGIN.FEATURE_AI_DESC'),
        icon: faStar
      },
      {
        title: this.translate.instant('LOGIN.FEATURE_BOOK'),
        description: this.translate.instant('LOGIN.FEATURE_BOOK_DESC'),
        icon: faCalendar
      },
      {
        title: this.translate.instant('LOGIN.FEATURE_MAP'),
        description: this.translate.instant('LOGIN.FEATURE_MAP_DESC'),
        icon: faMap
      },
      {
        title: this.translate.instant('LOGIN.FEATURE_SEC'),
        description: this.translate.instant('LOGIN.FEATURE_SEC_DESC'),
        icon: faShieldAlt
      }
    ];
  });

  readonly recaptchaContainerId = 'login-recaptcha';

  loading = false;
  errorMessage: string | null = null;
  infoMessage: string | null = null;
  phoneStep: PhoneStep = 'idle';
  returnUrl = '';
  private registrationRedirectTimer: number | null = null;

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
  }

  ngOnDestroy(): void {
    if (this.registrationRedirectTimer) {
      window.clearTimeout(this.registrationRedirectTimer);
    }
  }

  get rememberMe(): boolean {
    return !!this.loginForm.get('rememberMe')?.value;
  }

  get usesWhatsAppOtp(): boolean {
    return this.authService.usesWhatsAppOtp;
  }

  onSubmit(): void {
    this.errorMessage = null;
    this.infoMessage = null;

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const { email, password } = this.loginForm.value;
    this.loading = true;
    this.cdr.markForCheck();

    this.authService.login(email, password, this.rememberMe)
      .pipe(
        switchMap(user => this.handlePostFirebaseLogin(user)),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => this.navigateToHome(),
        error: (err) => this.handleError(err)
      });
  }

  signInWith(option: SignInOption): void {
    switch (option.action) {
      case 'phone-code':
        this.phoneStep = 'sending';
        this.errorMessage = null;
        this.infoMessage = null;
        this.cdr.markForCheck();
        break;
      case 'microsoft':
      case 'google':
      case 'facebook':
        this.loading = true;
        this.authService.signInWithSocialProvider(option.action, this.rememberMe)
          .pipe(
            switchMap(user => this.handlePostFirebaseLogin(user)),
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => this.navigateToHome(),
            error: (err) => this.handleError(err)
          });
        break;
      case 'create-account':
        this.router.navigate(['/register']);
        break;
      default:
        this.errorMessage = 'Unsupported sign-in method.';
        this.cdr.markForCheck();
    }
  }

  onForgotPassword(): void {
    const email = this.loginForm.get('email')?.value?.trim();
    const extras = email ? { queryParams: { email } } : {};
    this.router.navigate(['/forgot-password'], extras);
  }

  sendPhoneCode(): void {
    const phoneControl = this.loginForm.get('phone');
    if (!phoneControl?.value) {
      this.errorMessage = 'Please enter a phone number.';
      this.cdr.markForCheck();
      return;
    }

    phoneControl.markAsTouched();
    if (phoneControl.invalid) {
      this.errorMessage = 'Please enter a valid phone number with country code (e.g., +1234567890).';
      this.cdr.markForCheck();
      return;
    }

    const phoneNumber = this.normalizePhoneNumber(phoneControl.value);
    if (!this.isE164PhoneNumber(phoneNumber)) {
      this.errorMessage = 'Please enter a valid phone number with country code (e.g., +966501234567).';
      this.cdr.markForCheck();
      return;
    }

    phoneControl.setValue(phoneNumber, { emitEvent: false });
    this.loading = true;
    this.errorMessage = null;
    this.infoMessage = null;
    this.cdr.markForCheck();

    this.authService.signInWithPhoneNumber(phoneNumber, this.recaptchaContainerId, this.rememberMe)
      .pipe(
        tap(() => {
          this.phoneStep = 'code-sent';
          this.infoMessage = this.usesWhatsAppOtp
            ? 'Verification code sent through WhatsApp.'
            : 'Verification code sent to your phone.';
          this.loading = false;
        }),
        catchError(err => {
          this.handleError(err);
          this.loading = false;
          return of(null);
        }),
        finalize(() => this.cdr.markForCheck())
      )
      .subscribe();
  }

  verifyPhoneCode(): void {
    this.errorMessage = null;
    this.infoMessage = null;

    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const code = this.otpForm.get('code')?.value;
    this.phoneStep = 'verifying';
    this.loading = true;
    this.cdr.markForCheck();

    this.authService.verifyOtp(code)
      .pipe(
        switchMap(user => this.handlePostFirebaseLogin(user)),
        finalize(() => {
          this.phoneStep = 'idle';
          this.loading = false;
          this.otpForm.reset();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => this.navigateToHome(),
        error: (err) => {
          this.handleError(err);
          this.phoneStep = 'code-sent';
          this.cdr.markForCheck();
        }
      });
  }

  resendOtp(): void {
    const phoneNumber = this.normalizePhoneNumber(this.loginForm.get('phone')?.value);
    if (!phoneNumber) {
      this.errorMessage = 'Phone number is missing. Please start over.';
      this.phoneStep = 'idle';
      this.cdr.markForCheck();
      return;
    }

    this.errorMessage = null;
    this.infoMessage = 'Resending verification code...';
    this.phoneStep = 'sending';
    this.loading = true;
    this.cdr.markForCheck();

    this.authService.signInWithPhoneNumber(phoneNumber, this.recaptchaContainerId, this.rememberMe)
      .pipe(
        tap(() => {
          this.phoneStep = 'code-sent';
          this.infoMessage = this.usesWhatsAppOtp
            ? 'Verification code resent through WhatsApp.'
            : 'Verification code resent to your phone.';
        }),
        catchError(err => {
          this.handleError(err);
          this.phoneStep = 'idle';
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  cancelPhoneAuth(): void {
    this.phoneStep = 'idle';
    this.errorMessage = null;
    this.infoMessage = null;
    this.loading = false;
    this.otpForm.reset();
    this.loginForm.get('phone')?.reset();
    this.cdr.markForCheck();
  }

  private handlePostFirebaseLogin(user: User | null): Observable<void> {
    if (!user) {
      return throwError(() => new Error('No user information returned from provider.'));
    }
    this.sessionStorageService.saveUser(user);
    return this.loginToBackend(user);
  }

  private normalizePhoneNumber(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    const compact = value.replace(/[\s().-]/g, '');
    return compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
  }

  private isE164PhoneNumber(phoneNumber: string): boolean {
    return /^\+[1-9]\d{1,14}$/.test(phoneNumber);
  }

  private loginToBackend(user: User): Observable<void> {
    return this.authService.getCurrentUserAccessToken().pipe(
      switchMap(token => {
        this.sessionStorageService.saveAccessToken(token, new Date().getTime());
        return this.customerService.getCustomerByFirebaseId(user.uid).pipe(
          switchMap(response => {
            const customer = response.data;

            if (!customer) {
              return throwError(() => new Error(NO_CUSTOMER_PROFILE_ERROR));
            }

            this.sessionStorageService.saveCustomer(customer);

            const roles$ = this.customerService.getRolesByCustomerId(customer.id).pipe(
              tap(rolesData => {
                customer.roles = rolesData;
                this.sessionStorageService.saveCustomer(customer);
              }),
              catchError(err => {
                console.warn('Failed to load customer roles:', err);
                return of(null);
              })
            );

            const agency$ = customer.company?.transportationAgencyId
              ? this.transportationAgencyService.findTransportationAgencyByFilter(undefined, undefined, customer.company.transportationAgencyId, undefined, undefined).pipe(
                tap(agencyWrapperData => this.sessionStorageService.saveTransportationAgency(agencyWrapperData.data)),
                catchError(err => {
                  console.warn('Failed to load transportation agency:', err);
                  return of(null);
                })
              )
              : of(null);

            const forwarder$ = customer.company?.freightForwarderId
              ? this.freightForwarderService.getFreightForwarder(undefined, customer.company.freightForwarderId, undefined, undefined, 'en-US').pipe(
                tap(freightForwarder => this.sessionStorageService.saveFreightForwarder(freightForwarder)),
                catchError(err => {
                  console.warn('Failed to load freight forwarder:', err);
                  return of(null);
                })
              )
              : of(null);

            return forkJoin([roles$, agency$, forwarder$]).pipe(
              switchMap(() => this.baseMasterDataService.getBaseMasterData("en-US").pipe(
                tap(baseMasterData => {
                  this.sessionStorageService.saveBaseMasterData(baseMasterData.data);
                  this.enterpriseFeatureService.getAllSubscriptions();
                  this.toolbarService.updateLoggedInStatus(true);
                  this.toolbarService.updateCustomer(customer);
                })
              )),
              map(() => void 0)
            );
          })
        );
      }),
      catchError(err => {
        this.toolbarService.updateLoggedInStatus(false);
        this.toolbarService.updateCustomer(null);
        return throwError(() => err);
      })
    );
  }

  private navigateToHome(): void {
    if (this.returnUrl && this.returnUrl !== '/') {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    this.router.navigate(['/loads']);
  }

  private handleError(error: unknown): void {
    console.error('Authentication error:', error);
    this.errorMessage = this.mapAuthError(error);
    this.infoMessage = null;
    this.loading = false;

    if (this.isMissingAccountError(error)) {
      this.infoMessage = 'Redirecting you to registration...';
      this.redirectToRegistrationAfterMessage();
    }

    this.cdr.markForCheck();
  }

  private mapAuthError(error: unknown): string {
    if (this.isMissingAccountError(error)) {
      return this.translate.instant('AUTH_ERRORS.NO_ACCOUNT_PROFILE');
    }

    if (typeof error === 'object' && error !== null && 'code' in error) {
      const firebaseError = error as { code: string; message?: string };
      switch (firebaseError.code) {
        case 'auth/invalid-phone-number':
          return this.translate.instant('AUTH_ERRORS.INVALID_PHONE');
        case 'auth/missing-phone-number':
          return this.translate.instant('AUTH_ERRORS.MISSING_PHONE');
        case 'auth/captcha-check-failed':
          return this.translate.instant('AUTH_ERRORS.CAPTCHA_CHECK_FAILED');
        case 'auth/missing-app-credential':
          return this.translate.instant('AUTH_ERRORS.MISSING_APP_CREDENTIAL');
        case 'auth/quota-exceeded':
          return this.translate.instant('AUTH_ERRORS.QUOTA_EXCEEDED');
        case 'auth/operation-not-allowed':
          return this.translate.instant('AUTH_ERRORS.OPERATION_NOT_ALLOWED');
        case 'auth/unauthorized-domain':
          return this.translate.instant('AUTH_ERRORS.UNAUTHORIZED_DOMAIN');
        case 'auth/invalid-verification-code':
          return this.translate.instant('AUTH_ERRORS.INVALID_CODE');
        case 'auth/code-expired':
          return this.translate.instant('AUTH_ERRORS.CODE_EXPIRED');
        case 'auth/too-many-requests':
          return this.translate.instant('AUTH_ERRORS.TOO_MANY_REQUESTS');
        case 'auth/user-disabled':
          return this.translate.instant('AUTH_ERRORS.USER_DISABLED');
        case 'auth/invalid-email':
          return this.translate.instant('AUTH_ERRORS.INVALID_EMAIL');
        case 'auth/user-not-found':
          return this.translate.instant('AUTH_ERRORS.USER_NOT_FOUND');
        case 'auth/wrong-password':
          return this.translate.instant('AUTH_ERRORS.WRONG_PASSWORD');
        case 'auth/email-already-in-use':
          return this.translate.instant('AUTH_ERRORS.EMAIL_ALREADY_IN_USE');
        case 'auth/weak-password':
          return this.translate.instant('AUTH_ERRORS.WEAK_PASSWORD');
        case 'auth/network-request-failed':
          return this.translate.instant('AUTH_ERRORS.NETWORK_ERROR');
        case 'auth/popup-closed-by-user':
          return this.translate.instant('AUTH_ERRORS.POPUP_CLOSED');
        case 'auth/cancelled-popup-request':
          return this.translate.instant('AUTH_ERRORS.POPUP_CANCELLED');
        default:
          return firebaseError.message || this.translate.instant('AUTH_ERRORS.GENERIC');
      }
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return this.translate.instant('AUTH_ERRORS.GENERIC');
  }

  private isMissingAccountError(error: unknown): boolean {
    if (error instanceof Error) {
      return error.message === NO_CUSTOMER_PROFILE_ERROR || error.message.includes('No customer profile found');
    }

    if (typeof error === 'object' && error !== null && 'code' in error) {
      return (error as { code?: string }).code === 'auth/user-not-found';
    }

    return false;
  }

  private redirectToRegistrationAfterMessage(): void {
    if (this.registrationRedirectTimer) {
      window.clearTimeout(this.registrationRedirectTimer);
    }

    const email = this.loginForm.get('email')?.value;
    const queryParams: Record<string, string> = {};
    if (email) {
      queryParams['email'] = email;
    }
    if (this.authService.hasCurrentUser()) {
      queryParams['firebaseUser'] = 'current';
    }

    this.registrationRedirectTimer = window.setTimeout(() => {
      this.router.navigate(['/register'], { queryParams });
    }, REGISTRATION_REDIRECT_DELAY_MS);
  }
}
